import { type CsticId } from "~/features/configurator/Linear/models";
import { type SubstepType } from "~/features/configurator/Linear/dtos";
import { selectCsticExpanded } from "~/features/configurator/Linear/redux/selectCstic";
import { updateCsticValues, updateSubstepAdditionalData } from "~/features/configurator/Linear/redux/actions";
import { type NumberFormatEvent } from "~/common/components/CustomNumberFormat";
import { useDispatch, useSelector } from "react-redux";
import { type ReactNode, useCallback } from "react";
import { isNonNull } from "~/common/utils/reducers";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import { useDebouncedState } from "~/common/hooks/useDebouncedValue";

interface Props {
  csticId: CsticId;
  substep: SubstepType;
  //@deprecated use csticsToSet instead
  csticsToNull?: string[];
  csticsToSet?: Array<CsticToSet | Resolver>;
  type: "number" | "string";
  required?: boolean;
  error?: boolean;
  useCsticLabel?: boolean;
}

type CsticToSet =
  | {
      key: string;
      value: string | null;
    }
  | undefined;

type Resolver = (value: string | null) => CsticToSet;

type NumberProps = Props & {
  type: "number";
};

interface NumberResult {
  value: number | null;
  handleState: (e: NumberFormatEvent) => void;
  onBlur: () => void;
  error: boolean;
  label?: ReactNode;
}

type StringProps = Props & {
  type: "string";
};

interface StringResult {
  value: string | null;
  onChange: (e: string | null) => void;
  onBlur: () => void;
  error: boolean;
  label?: ReactNode;
}

export function useCsticInputProps(props: NumberProps): NumberResult;
export function useCsticInputProps(props: StringProps): StringResult;

/**
 * This hook provides all props necessary to connect a cstic to an input
 * @param csticId - The cstic to connect
 * @param substep - The substep the input is in, needed for updateCsticValues action
 * @param csticsToNull - Cstics that should be set to null when the input is changed
 * @param csticsToSet - Cstics that should be set to a specific value when the input is changed
 */
export function useCsticInputProps({
  csticId,
  substep,
  csticsToNull = [],
  csticsToSet = [],
  type,
  required,
  error: externalError,
  useCsticLabel,
}: Props): NumberResult | StringResult {
  const { cstic, isInConflict, isDirty } = useSelector(selectCsticExpanded(csticId, substep));
  const dispatch = useDispatch();

  const externalOnChange = useCallback((value: string | null) => {
    dispatch(
      updateCsticValues(
        [
          { ...csticId, value },
          ...csticsToNull.map((key) => ({
            ...csticId,
            key,
            value: null,
          })),
          ...resolveCsticsToSet(csticsToSet, value)
            .filter(isNonNull)
            .map(({ key, value }) => ({
              ...csticId,
              key,
              value,
            })),
        ],
        substep,
      ),
    );
    dispatch(
      updateSubstepAdditionalData(
        [
          {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            value: true as any,
            ...csticId,
          },
        ],
        substep,
      ),
    );
  }, []);

  const [value, onChange, flush] = useDebouncedState({ value: cstic?.value ?? null, onChange: externalOnChange });
  const label = useCsticLabel ? (
    <LanguageAwareExternalContent content={cstic?.label} className={"scha__lc__external-label"} />
  ) : undefined;

  if (type === "string") {
    const returnValue = {
      error: externalError || (isDirty && (isInConflict || (required && !value))),
      value: value ?? "",
      onChange,
      onBlur: flush,
    } as StringResult;
    if (useCsticLabel) {
      returnValue.label = label;
    }
    return returnValue;
  } else {
    // type === "number"
    const returnValue = {
      error: externalError || (isDirty && (isInConflict || (required && !value))),
      value: value !== null && value !== "" ? +value : null,
      handleState: (e: NumberFormatEvent) => onChange(e.value || null),
      onBlur: flush,
    } as NumberResult;
    if (useCsticLabel) {
      returnValue.label = label;
    }
    return returnValue;
  }
}

function resolveCsticsToSet(csticsToSet: Array<CsticToSet | Resolver>, value: string | null) {
  return csticsToSet.map((csticToSet) => {
    if (typeof csticToSet === "function") {
      return csticToSet(value);
    }
    return csticToSet;
  });
}
